import { defineInvokeEventa } from '@moeru/eventa'
import { maxLength, minLength, object, optional, pipe, string } from 'valibot'

/** One GPT-SoVITS runtime shared by every character voice. */
export const characterVoiceModelId = 'gpt-sovits'

/** Voice selected before the user picks another character. */
export const characterVoiceDefaultVoiceId = 'natsume-casual'

/** Fixed Japanese sentence used by the settings preview. */
export const characterVoicePreviewText = 'おかえり。今日は何をしていたの？'

export interface CharacterVoice {
  id: 'natsume-casual' | 'natsume-romantic' | 'atri'
  name: string
  gender: 'female'
  languageCode: 'ja'
  languageTitle: string
}

/**
 * Voices the desktop can speak.
 * Weights and reference audio stay in the main process.
 */
export const characterVoices = [
  { id: 'natsume-casual', name: 'Natsume Casual', gender: 'female', languageCode: 'ja', languageTitle: 'Japanese' },
  { id: 'natsume-romantic', name: 'Natsume Romantic', gender: 'female', languageCode: 'ja', languageTitle: 'Japanese' },
  { id: 'atri', name: 'ATRI', gender: 'female', languageCode: 'ja', languageTitle: 'Japanese' },
] as const satisfies readonly CharacterVoice[]

/** Returns one catalog voice, or `undefined` when the id is not a character voice. */
export function characterVoiceById(voiceId: string): CharacterVoice | undefined {
  return characterVoices.find(voice => voice.id === voiceId)
}

export const characterVoiceSynthesizeRequestSchema = object({
  text: pipe(string(), minLength(1), maxLength(2000)),
  voiceId: pipe(string(), minLength(1), maxLength(64)),
  turnId: optional(pipe(string(), minLength(1), maxLength(80))),
})

export interface CharacterVoiceSynthesisMetrics {
  modelId: string
  voiceId: string
  loadMs: number
  switchMs: number
  synthesisMs: number
  audioDurationMs: number
  rtf: number
  sampleRate: number
  wavBytes: number
  /** True only for the call that started the sidecar process. */
  coldStart: boolean
  /** True when this call loaded different weights. */
  voiceSwitch: boolean
  /** True when this call paid neither a process start nor a weight switch. */
  warmPath: boolean
  requestAt: number
  sidecarReadyAt: number
  weightsReadyAt: number
  synthesisStartedAt: number
  audioReadyAt: number
}

export interface CharacterVoiceSynthesisResult {
  wavBase64: string
  metrics: CharacterVoiceSynthesisMetrics
}

export interface CharacterVoiceLoadResult {
  loadMs: number
  modelId: string
}

export interface CharacterVoicePrepareResult {
  modelId: string
  voiceId: string
  loadMs: number
  switchMs: number
  coldStart: boolean
  voiceSwitch: boolean
  weightsReady: true
}

export const characterVoicePrepareRequestSchema = object({
  voiceId: pipe(string(), minLength(1), maxLength(64)),
})

export const electronCharacterVoiceSynthesize = defineInvokeEventa<
  CharacterVoiceSynthesisResult,
  { text: string, voiceId: string, turnId?: string }
>('eventa:invoke:velis:character-voice:synthesize')

export const electronCharacterVoiceEnsureLoaded = defineInvokeEventa<CharacterVoiceLoadResult>(
  'eventa:invoke:velis:character-voice:ensure-loaded',
)

export const electronCharacterVoicePrepare = defineInvokeEventa<
  CharacterVoicePrepareResult,
  { voiceId: string }
>('eventa:invoke:velis:character-voice:prepare')
